import { FC, ReactNode } from 'react'

import { StatusChip, StatusChipSizes } from '@inno-ui/status-chip'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'

import { REVIEW_STATUS_COLORS } from '../../../reviewStatusColors'
import { AiReviewResult, ArtifactSummary } from '../../../types'
import styles from '../ReviewQueue.module.scss'

interface CardShellProps {
  artifact: ArtifactSummary
  // verdict is optional — only set when an AI review has run; shown as a chip
  // next to the status chip regardless of pending/approved state.
  verdict: AiReviewResult | null
  // Right-hand action buttons (reveal / AI review / view code). The caller
  // decides which to show — RevokeCard omits AI review and hosts editor.
  actions: ReactNode
  children: ReactNode
}

// Shared chrome for the preview + code modals — keeps the two surfaces in
// lockstep when the project changes modal conventions.
export const REVIEW_MODAL_CHROME = {
  closeOnClickOutside: true,
  showCloseButton: true,
  zIndex: 9999,
  contentFullWidth: true
} as const

// AI-review verdicts share their colour AND label. Single source of truth so
// the chip and the badge never disagree on what `pass`/`flag`/`unknown` mean.
const VERDICT_META: Record<
  NonNullable<AiReviewResult['verdict']>,
  {
    color: string
    labelKey: 'ai_verdict_pass' | 'ai_verdict_flag' | 'ai_verdict_unknown'
  }
> = {
  pass: { color: '#2e7d5b', labelKey: 'ai_verdict_pass' },
  flag: { color: '#c0392b', labelKey: 'ai_verdict_flag' },
  unknown: { color: '#9e9e9e', labelKey: 'ai_verdict_unknown' }
}

// Outer card chrome: title + status + verdict chip on the left, action buttons
// on the right, and an arbitrary lower section rendered as children. Owns the
// accent stripe colour and the head layout — everything else lives below.
export const CardShell: FC<CardShellProps> = ({
  artifact,
  verdict,
  actions,
  children
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const isApproved = artifact.review_status === 'approved'
  const verdictMeta = verdict?.verdict ? VERDICT_META[verdict.verdict] : null
  const findings = verdict?.findings ?? []
  const hasAiFeedback = Boolean(verdict?.summary) || findings.length > 0

  return (
    <div
      className={cn(styles.card, {
        [styles.cardApproved]: isApproved,
        [styles.cardPending]: !isApproved
      })}
    >
      <div className={styles.head}>
        <div className={styles.headMain}>
          <div className={styles.titleRow}>
            <span className={styles.title}>{artifact.title}</span>
            <StatusChip
              label={isApproved ? t('status_approved') : t('status_pending')}
              color={
                isApproved
                  ? REVIEW_STATUS_COLORS.approved
                  : REVIEW_STATUS_COLORS.pending
              }
              size={StatusChipSizes.XS}
              fixedWidth={false}
            />
            {verdictMeta && (
              <StatusChip
                label={t(verdictMeta.labelKey)}
                color={verdictMeta.color}
                size={StatusChipSizes.XS}
                fixedWidth={false}
              />
            )}
          </div>
          {artifact.owner_name && (
            <span className={styles.creator}>
              {t('review_created_by', { name: artifact.owner_name })}
            </span>
          )}
        </div>
        <div className={styles.headActions}>{actions}</div>
      </div>
      {hasAiFeedback && (
        <div className={styles.aiPanel}>
          {verdict?.summary && (
            <div className={styles.aiSummary}>
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                {verdict.summary}
              </ReactMarkdown>
            </div>
          )}
          {findings.length > 0 && (
            <ul className={styles.findings}>
              {findings.map((finding, index) => {
                const label = [finding.severity, finding.type]
                  .filter(Boolean)
                  .join(' · ')
                const detail = finding.explanation || finding.evidence
                return (
                  <li key={index} className={styles.finding}>
                    {label && (
                      <span className={styles.findingType}>{label}</span>
                    )}
                    {detail && (
                      <div className={styles.findingText}>
                        <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                          {detail}
                        </ReactMarkdown>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
      {children}
    </div>
  )
}
